/**
 * SaveSystem — versioned local persistence.
 *
 * • Storage: localStorage (game state is small pure-JSON; IndexedDB is
 *   overkill at this size and localStorage is synchronous + reliable here).
 * • Integrity: FNV-1a checksum over the serialized blob detects corruption
 *   and casual tampering. This is integrity, NOT security — a client-side
 *   game can always be hacked locally; leaderboards (future) must validate
 *   server-side via replay verification against the seed.
 * • Migrations: SAVE_VERSION gates `migrate()` — bump the version and add a
 *   step when the schema changes.
 */

import type { GameState } from './types';

export const SAVE_VERSION = 2;
const STORAGE_KEY = 'met:save:v1';

function checksum(json: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

interface SaveEnvelope {
  v: number;
  ts: number;
  data: string; // serialized GameState
  ck: string; // checksum of data
}

export function serializeSave(s: GameState): string {
  const json = JSON.stringify(s);
  const env: SaveEnvelope = { v: SAVE_VERSION, ts: Date.now(), data: json, ck: checksum(json) };
  return JSON.stringify(env);
}

export function persistSave(s: GameState): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, serializeSave(s));
    return true;
  } catch {
    // Storage full/blocked — fail soft; autosaver retries next cycle.
    return false;
  }
}

export type LoadResult =
  | { ok: true; state: GameState }
  | { ok: false; reason: 'none' | 'corrupt' | 'version' };

export function loadSave(): LoadResult {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return { ok: false, reason: 'none' };
  }
  if (!raw) return { ok: false, reason: 'none' };
  try {
    const env = JSON.parse(raw) as SaveEnvelope;
    if (env.ck !== checksum(env.data)) return { ok: false, reason: 'corrupt' };
    let state = JSON.parse(env.data) as GameState;
    state = migrate(state);
    if (state.meta.version !== SAVE_VERSION) return { ok: false, reason: 'version' };
    return { ok: true, state };
  } catch {
    return { ok: false, reason: 'corrupt' };
  }
}

export function clearSave(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function hasSave(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/** Forward-migrate old saves. Each step upgrades exactly one version. */
function migrate(s: GameState): GameState {
  let state = s;
  // v1 → v2: showrooms introduced.
  if (state.meta.version < 2) {
    if (!Array.isArray(state.showrooms)) state.showrooms = [];
    state.meta.version = 2;
  }
  return state;
}

/** Download a JSON export (uses Blob URL — CSP img/media unaffected). */
export function exportSaveFile(s: GameState): void {
  const blob = new Blob([serializeSave(s)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mattress-empire-save-day${s.time.day}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Import from a user-picked file; validates checksum before accepting. */
export function importSaveFile(file: File): Promise<GameState> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const env = JSON.parse(String(reader.result)) as SaveEnvelope;
        if (env.ck !== checksum(env.data)) throw new Error('Checksum mismatch — file corrupted');
        let state = JSON.parse(env.data) as GameState;
        state = migrate(state);
        if (state.meta.version !== SAVE_VERSION) throw new Error('Unsupported save version');
        resolve(state);
      } catch (e) {
        reject(e instanceof Error ? e : new Error('Invalid save file'));
      }
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsText(file);
  });
}
